import { useRef } from "react"
import { Plus, X } from "lucide-react"

import { DatePicker } from "@/components/DatePicker"
import { FormCard } from "@/components/FormCard"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Field, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import type { Conversion, ReequipmentTabState } from "@/lib/documentEditor"

function emptyConversion(id: number): Conversion {
  return {
    id,
    description: "",
    approved: false,
    docName: "",
    docNumber: "",
    docDate: "",
  }
}

export function ReequipmentTab({
  value,
  onChange,
  onCommit,
}: {
  value: ReequipmentTabState
  onChange: (patch: Partial<ReequipmentTabState>) => void
  /** Autosaves the full reconstructions list immediately (radio/checkbox/date/add/remove) — text fields autosave on blur instead. */
  onCommit: () => void
}) {
  // Monotonic counter so removing a middle entry can't re-associate the
  // surviving rows' state (document-editor.md's DE9).
  const nextId = useRef(1)

  function setHasReequipment(next: boolean) {
    if (next && value.conversions.length === 0) {
      onChange({
        hasReequipment: true,
        conversions: [emptyConversion(nextId.current++)],
      })
      onCommit()
      return
    }
    onChange({ hasReequipment: next })
    onCommit()
  }

  function addConversion() {
    onChange({
      conversions: [...value.conversions, emptyConversion(nextId.current++)],
    })
    onCommit()
  }

  function removeConversion(id: number) {
    onChange({
      conversions: value.conversions.filter((c) => c.id !== id),
    })
    onCommit()
  }

  function updateConversion(id: number, patch: Partial<Conversion>) {
    onChange({
      conversions: value.conversions.map((c) =>
        c.id === id ? { ...c, ...patch } : c
      ),
    })
  }

  return (
    <FormCard title="Переобладнання">
      <Field required>
        <FieldLabel>Існує переобладнання</FieldLabel>
        <RadioGroup
          className="flex gap-4"
          onValueChange={(next) => setHasReequipment(next === "yes")}
          value={
            value.hasReequipment === null
              ? ""
              : value.hasReequipment
                ? "yes"
                : "no"
          }
        >
          <label className="flex items-center gap-2 text-sm" htmlFor="reequipment-no">
            <RadioGroupItem id="reequipment-no" value="no" />
            Ні
          </label>
          <label className="flex items-center gap-2 text-sm" htmlFor="reequipment-yes">
            <RadioGroupItem id="reequipment-yes" value="yes" />
            Так
          </label>
        </RadioGroup>
      </Field>

      {value.hasReequipment === true && (
        <div className="mt-4 grid gap-6">
          {value.conversions.map((conversion, index) => (
            <div className="grid gap-4" key={conversion.id}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  Переобладнання {index + 1}
                </p>
                <Button
                  aria-label={`Видалити переобладнання ${index + 1}`}
                  onClick={() => removeConversion(conversion.id)}
                  size="icon"
                  variant="outline"
                >
                  <X />
                </Button>
              </div>
              <Field>
                <FieldLabel htmlFor={`reequipment-description-${conversion.id}`}>
                  Суть переобладнання
                </FieldLabel>
                <Textarea
                  id={`reequipment-description-${conversion.id}`}
                  onBlur={onCommit}
                  onChange={(e) =>
                    updateConversion(conversion.id, {
                      description: e.target.value,
                    })
                  }
                  placeholder="Опис внесеного переобладнання"
                  rows={3}
                  value={conversion.description}
                />
              </Field>
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor={`reequipment-approved-${conversion.id}`}
              >
                <Checkbox
                  checked={conversion.approved}
                  id={`reequipment-approved-${conversion.id}`}
                  onCheckedChange={(checked) => {
                    updateConversion(conversion.id, {
                      approved: checked === true,
                    })
                    onCommit()
                  }}
                />
                Переобладнання погоджено
              </label>
              {conversion.approved && (
                <div className="grid grid-cols-1 gap-4 @[544px]:grid-cols-2 @[832px]:grid-cols-3">
                  <Field>
                    <FieldLabel htmlFor={`reequipment-doc-name-${conversion.id}`}>
                      Назва документа
                    </FieldLabel>
                    <Input
                      id={`reequipment-doc-name-${conversion.id}`}
                      onBlur={onCommit}
                      onChange={(e) =>
                        updateConversion(conversion.id, {
                          docName: e.target.value,
                        })
                      }
                      placeholder="напр. Висновок про технічну можливість переобладнання"
                      value={conversion.docName}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`reequipment-doc-number-${conversion.id}`}>
                      Номер документа
                    </FieldLabel>
                    <Input
                      id={`reequipment-doc-number-${conversion.id}`}
                      onBlur={onCommit}
                      onChange={(e) =>
                        updateConversion(conversion.id, {
                          docNumber: e.target.value,
                        })
                      }
                      placeholder="№"
                      value={conversion.docNumber}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`reequipment-doc-date-${conversion.id}`}>
                      Дата документа
                    </FieldLabel>
                    <DatePicker
                      id={`reequipment-doc-date-${conversion.id}`}
                      onChange={(next) => {
                        updateConversion(conversion.id, { docDate: next })
                        onCommit()
                      }}
                      value={conversion.docDate}
                    />
                  </Field>
                </div>
              )}
            </div>
          ))}
          <Button className="w-fit" onClick={addConversion} variant="outline">
            <Plus data-icon="inline-start" />
            Додати переобладнання
          </Button>
        </div>
      )}
    </FormCard>
  )
}
